import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";

import {
    CheckboxInput,
    CheckboxInputProps,
    CheckboxInputOption,
} from "components/form/legacyFields/checkboxInput";
import * as CT from "org/risks/content/types";
import * as R from "org/risks/types";
import { mapForm, simpleMappers, WrappedFormUtils } from "utils/form";

import * as U from "../content/utils";

import * as theme from "./theme.css";
import * as T from "./types";

type CheckboxGroupMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.CheckboxGroupInstance
> & { columns: number };

export default class CheckboxGroupMetadataField<
    ROOT,
> extends React.PureComponent<CheckboxGroupMetadataFieldProps<ROOT>> {
    render = () => {
        const { id, label, instance, columns } = this.props;
        const { fieldType } = instance;
        const baseProps = {
            id,
            className: theme.checkboxGroupMetadataField,
            label,
            options: fieldType.options.map(mapOptionValueToCheckboxInputOption),
            columns,
        };
        let props: CheckboxInputProps<string, ROOT>;
        switch (this.props.type) {
            case "CREATE":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    initialValue: undefined,
                    form: mapInstanceFormToOptionIdsForm<ROOT>(
                        id,
                        this.props.form,
                        instance
                    ),
                };
                break;
            case "EDIT": {
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: instance.value?.map((v) => v.id),
                    onChange: this.handleOnChange,
                    validationError: this.props.validationError,
                };
                break;
            }
            case "READ_ONLY":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: instance.value?.map((v) => v.id),
                };
                break;
            default:
                return assertNever(this.props);
        }
        return <CheckboxInput {...props} />;
    };

    private handleOnChange = (value: string[] | undefined) => {
        const { instance } = this.props;
        if (this.props.type === "EDIT") {
            return this.props.onChangeAsync(
                Promise.resolve(value),
                (_prev, val) =>
                    U.extractMetadataInstanceValue(
                        optionIdsToInstance(val, instance)
                    )
            );
        }
        return Promise.reject(
            new Error("Cannot change value in non-edit mode")
        );
    };
}

function mapOptionValueToCheckboxInputOption(
    optionValue: R.OptionValue
): CheckboxInputOption {
    return {
        value: optionValue.id,
        label: optionValue.value,
    };
}

function instanceToOptionIds(
    instance: CT.MetadataInstance
): string[] | undefined {
    if (instance.type !== "CheckboxGroupInstance") {
        throw new Error(
            "Expected checkbox group instance, got " + instance.type
        );
    }
    const { value } = instance;
    return value?.map((v) => v.id);
}

function optionIdsToInstance(
    array: string[] | undefined,
    instance: CT.MetadataInstance
): CT.MetadataInstance {
    if (instance.type !== "CheckboxGroupInstance") {
        throw new Error(
            "Expected checkbox group instance, got " + instance.type
        );
    }
    const { options } = instance.fieldType;
    let newValue: R.OptionValue[] | undefined;
    if (array !== undefined) {
        newValue = options.filter((opt) => array.indexOf(opt.id) >= 0);
        if (newValue.length !== array.length) {
            throw new Error(
                "The number of options should be the same as the number of option ids"
            );
        }
    } else {
        newValue = undefined;
    }

    return {
        ...instance,
        value: newValue,
    };
}

function mapInstanceFormToOptionIdsForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >,
    instance: CT.MetadataInstance
): WrappedFormUtils<
    {
        [x: string]: string[];
    },
    ROOT
> {
    const mapper = simpleMappers<
        string,
        CT.MetadataInstance,
        string[] | undefined,
        ROOT
    >(id, instanceToOptionIds, (optionIds) =>
        optionIdsToInstance(optionIds, instance)
    );
    return mapForm<Record<string, string[]>>()(instanceForm, mapper());
}

export { CheckboxGroupMetadataField, type CheckboxGroupMetadataFieldProps };
